/**
 * WsEventSource — an EventSource over the app's WebSocket client.
 *
 * The server broadcasts EventBus events wrapped as
 *   { type: "feedback", message: { _type: "event", ...event } }
 * alongside MFP feedback frames. This source registers a message handler, filters
 * to the event frames, and forwards them as NormalizedEvents.
 *
 * It does NOT open or close the socket — RootLayout owns the singleton's
 * connect()/close() lifecycle. Because handlers live on the singleton (not the
 * underlying socket), this source keeps working across auto-reconnects.
 */

import { wsClient } from "../../api/ws.js";
import type { EventSource, NormalizedEvent } from "./types.js";

/** The narrow surface of the WS client this source depends on. */
interface EventClient {
  onMessage(handler: (data: unknown) => void): () => void;
}

export class WsEventSource implements EventSource {
  private unsubscribe: (() => void) | null = null;

  constructor(private readonly client: EventClient = wsClient) {}

  start(onEvent: (event: NormalizedEvent) => void): void {
    this.unsubscribe = this.client.onMessage((data) => {
      const frame = data as { type?: string; message?: Record<string, unknown> };
      if (frame?.type !== "feedback") return;
      const message = frame.message;
      if (!message || message._type !== "event") return;
      onEvent(message as unknown as NormalizedEvent);
    });
  }

  stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }
}
